"use client"
import React from 'react';
import { motion } from 'motion/react';
import { Rocket, Building2, Users, Globe, Check } from 'lucide-react';
import { TCompaniesConfig } from '@/app/config';

const SolutionsOrganizations = ({config}: {config: TCompaniesConfig["SolutionsOrganizations"]}) => {
  const organizationTypesIcons = [
    {
      icon: Rocket,
     
    },
    {
      icon: Building2,
     
    },
    {
      icon: Users,
    
    },
    {
      icon: Globe,
      
    }
  ];
  const organizationTypes = config?.organizationTypes 

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  
  const cardHoverVariants = {
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const titleHoverVariants = {
    hover: {
      scale: 1.05,
      color: "#16a34a",
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <motion.section 
      className="py-20 bg-white"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {config?.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            {config?.description}
          </motion.p>
        </motion.div>

        {/* Solutions Grid */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          variants={containerVariants}
        >
          {organizationTypes.map((org, index) => {
            const IconComponent = organizationTypesIcons[index].icon;
            return (
              <motion.div 
                key={index} 
                className="space-y-6 cursor-pointer"
                variants={ {
                  hidden: { 
                    opacity: 0, 
                    x: -50,
                    scale: 0.9
                  },
                  visible: {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }
                }}
                whileHover="hover"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                {...cardHoverVariants}
              >
                <motion.div 
                  className="flex items-start space-x-4"
                  variants={ {
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}
                >
                  <motion.div 
                    className={`w-16 h-16 ${org.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}
                    variants={ {
                      hidden: { 
                        scale: 0,
                        rotate: -90,
                        opacity: 0
                      },
                      visible: {
                        scale: 1,
                        rotate: 0,
                        opacity: 1,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                          delay: 0.3
                        }
                      },
                      hover: {
                        scale: 1.1,
                        rotate: [0, -5, 5, 0],
                        transition: {
                          duration: 0.3,
                          ease: "easeInOut"
                        }
                      }
                    }}
                    whileHover="hover"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <div className="flex-1">
                    <motion.h3 
                      className="text-2xl font-bold text-gray-900 mb-3"
                      variants={ {
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover={titleHoverVariants.hover}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.2 }}
                    >
                      {org.title}
                    </motion.h3>
                    
                    <motion.p 
                      className="text-gray-600 leading-relaxed mb-6"
                      variants={ {
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            ease: "easeOut"
                          }
                        }
                      }}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                    >
                      {org.description}
                    </motion.p>
                    
                    {/* Features */}
                    <motion.div 
                      className="grid grid-cols-2 gap-4"
                      variants={containerVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {org.features.map((feature, featureIndex) => (
                        <motion.div 
                          key={featureIndex} 
                          className="flex items-center space-x-2"
                          variants={{
                            hidden: { opacity: 0, x: -20 },
                            visible: {
                              opacity: 1,
                              x: 0,
                              transition: {
                                duration: 0.4,
                                ease: "easeOut"
                              }
                            },
                            hover: {
                              x: 5,
                              transition: {
                                duration: 0.2,
                                ease: "easeOut"
                              }
                            }
                          }}
                          whileHover="hover"
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 + featureIndex * 0.1 + 0.4 }}
                        >
                          <motion.div
                            variants={ {
                              hidden: { scale: 0, rotate: -180 },
                              visible: {
                                scale: 1,
                                rotate: 0,
                                transition: {
                                  type: "spring",
                                  stiffness: 260,
                                  damping: 20
                                }
                              },
                              hover: {
                                scale: 1.2,
                                rotate: 360,
                                transition: {
                                  duration: 0.3
                                }
                              }
                            }}
                            whileHover="hover"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 + featureIndex * 0.1 + 0.5 }}
                          >
                            <Check className="w-4 h-4 text-green-500" />
                          </motion.div>
                          <motion.span 
                            className="text-sm text-gray-600"
                            whileHover={{ color: "#16a34a" }}
                            transition={{ duration: 0.2 }}
                          >
                            {feature}
                          </motion.span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                </motion.div>

                {/* Hover background effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-gray-50 to-transparent rounded-2xl opacity-0 -z-10"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Decorative floating elements */}
        <motion.div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-3 h-3 rounded-full ${
                i % 2 === 0 ? 'bg-orange-200' : 'bg-blue-200'
              }`}
              style={{
                left: `${15 + i * 20}%`,
                top: `${20 + (i * 15) % 60}%`,
              }}
              animate={{
                y: [-30, 30, -30],
                x: [-10, 10, -10],
                opacity: [0.2, 0.6, 0.2],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut"
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default SolutionsOrganizations;